import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUsers, ApiUser, deleteUser, setTemporaryPassword, mapApiRoleToLabel } from '../../services/UserService';
import { getRoles, updateRole, RoleDto, getRolePermissions, updateRolePermissions, RolePermissionDto } from '../../services/RoleService';
import { getActivityLogs, ActivityLog } from '../../services/ActivityService';
import { formatDistanceToNow } from 'date-fns';

type ActivityUserScope = 'all' | 'active' | 'inactive';
type ActivityTimeRange = '24h' | '7d' | '30d';
type ActivityModuleFilter = 'all' | 'templates' | 'campaigns' | 'settings' | 'groups';

interface ActivityFilters {
  userScope: ActivityUserScope;
  timeframe: ActivityTimeRange;
  module: ActivityModuleFilter;
}

const activityTitles: Record<ActivityLog['activityType'], string> = {
  login: 'User Login',
  template_change: 'Template Change',
  campaign_change: 'Campaign Activity',
  settings_change: 'Settings Updated',
  group_change: 'Group Update'
};

const activityBadges: Record<ActivityLog['activityType'], string> = {
  login: 'Login',
  template_change: 'Templates',
  campaign_change: 'Campaigns',
  settings_change: 'Settings',
  group_change: 'Groups'
};

function getActivityTitle(activity: ActivityLog) {
  return activityTitles[activity.activityType] || 'Activity';
}

function getActivityBadge(activity: ActivityLog) {
  return activityBadges[activity.activityType] || 'Activity';
}

function getActivityDescription(activity: ActivityLog) {
  if (activity.description) return activity.description;
  const actor = activity.userName || 'System';
  switch (activity.activityType) {
    case 'login':
      return `${actor} logged in`;
    case 'template_change':
      return `${actor} updated a template`;
    case 'campaign_change':
      return `${actor} changed campaign settings`;
    case 'settings_change':
      return `${actor} modified system settings`;
    case 'group_change':
      return `${actor} updated a contact group`;
    default:
      return `${actor} performed an action`;
  }
}

function formatRelativeTime(value?: string) {
  if (!value) return 'Just now';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return 'Just now';
  }
}

const UserRoleManagement = () => {
  const [selectedTab, setSelectedTab] = useState('users');
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation() as { state: { userCreated?: boolean } };
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState<boolean>(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState<boolean>(false);
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [rolesLoading, setRolesLoading] = useState<boolean>(false);
  const [rolesError, setRolesError] = useState<string>('');
  const [editingRole, setEditingRole] = useState<RoleDto | null>(null);
  const [roleForm, setRoleForm] = useState<{ name: string; description: string }>({ name: '', description: '' });
  const [savingRole, setSavingRole] = useState<boolean>(false);
  const [roleSuccess, setRoleSuccess] = useState<boolean>(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [permLoading, setPermLoading] = useState<boolean>(false);
  const [permError, setPermError] = useState<string>('');
  const [permissions, setPermissions] = useState<RolePermissionDto[]>([]);
  const [savingPerms, setSavingPerms] = useState<boolean>(false);
  const [passwordModalUser, setPasswordModalUser] = useState<ApiUser | null>(null);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [settingPassword, setSettingPassword] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [passwordSuccess, setPasswordSuccess] = useState<boolean>(false);
  const [loginFilters, setLoginFilters] = useState<ActivityFilters>({ userScope: 'all', timeframe: '24h', module: 'all' });
  const [systemFilters, setSystemFilters] = useState<ActivityFilters>({ userScope: 'all', timeframe: '7d', module: 'all' });
  const [loginActivities, setLoginActivities] = useState<ActivityLog[]>([]);
  const [systemActivities, setSystemActivities] = useState<ActivityLog[]>([]);
  const [loginActivityLoading, setLoginActivityLoading] = useState<boolean>(false);
  const [systemActivityLoading, setSystemActivityLoading] = useState<boolean>(false);
  const [loginActivityError, setLoginActivityError] = useState<string>('');
  const [systemActivityError, setSystemActivityError] = useState<string>('');
  const [showLoginResults, setShowLoginResults] = useState<boolean>(false);
  const [showSystemResults, setShowSystemResults] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getUsers()
      .then((u) => { if (mounted) setUsers(u); })
      .catch((e) => { if (mounted) setError(e.message || 'Failed to load users'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if ((location.state as any)?.userCreated) {
      setShowSuccess(true);
      // Clear navigation state so it doesn't persist on refresh/back
      navigate('.', { replace: true, state: {} });
      const t = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [location.state, navigate]);
  useEffect(() => {
    let mounted = true;
    setRolesLoading(true);
    getRoles()
      .then((rs) => { if (mounted) setRoles(rs); })
      .catch((e) => { if (mounted) setRolesError(e.message || 'Failed to load roles'); })
      .finally(() => { if (mounted) setRolesLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setShowLoginResults(false);
    let cancelled = false;
    const loadLoginActivities = async () => {
      setLoginActivityLoading(true);
      setLoginActivityError('');
      try {
        const logs = await getActivityLogs({
          category: 'login',
          userScope: loginFilters.userScope,
          range: loginFilters.timeframe,
          module: loginFilters.module,
          limit: 5
        });
        if (!cancelled) {
          setLoginActivities(logs);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoginActivityError(e?.message || 'Failed to load login activity');
        }
      } finally {
        if (!cancelled) {
          setLoginActivityLoading(false);
        }
      }
    };

    loadLoginActivities();
    return () => {
      cancelled = true;
    };
  }, [loginFilters]);

  useEffect(() => {
    setShowSystemResults(false);
    let cancelled = false;
    const loadSystemActivities = async () => {
      setSystemActivityLoading(true);
      setSystemActivityError('');
      try {
        const logs = await getActivityLogs({
          category: 'system',
          userScope: systemFilters.userScope,
          range: systemFilters.timeframe,
          module: systemFilters.module,
          limit: 8
        });
        if (!cancelled) {
          setSystemActivities(logs);
        }
      } catch (e: any) {
        if (!cancelled) {
          setSystemActivityError(e?.message || 'Failed to load system activity');
        }
      } finally {
        if (!cancelled) {
          setSystemActivityLoading(false);
        }
      }
    };

    loadSystemActivities();
    return () => {
      cancelled = true;
    };
  }, [systemFilters]);

  const openEditRole = (r: RoleDto) => {
    setEditingRole(r);
    setRoleForm({ name: r.name, description: r.description || '' });
  };

  const selectRole = async (r: RoleDto) => {
    setSelectedRoleId(r.id);
    setPermLoading(true);
    setPermError('');
    try {
      const perms = await getRolePermissions(r.id);
      setPermissions(perms);
    } catch (e: any) {
      setPermError(e.message || 'Failed to load permissions');
    } finally {
      setPermLoading(false);
    }
  };

  const togglePerm = (module: string, key: 'can_read' | 'can_write') => {
    setPermissions((prev) => prev.map((p) => p.module === module ? { ...p, [key]: !Boolean(p[key]) } : p));
  };

  const savePermissions = async () => {
    if (!selectedRoleId) return;
    setSavingPerms(true);
    try {
      const updated = await updateRolePermissions(selectedRoleId, permissions.map(p => ({ ...p, can_read: Boolean(p.can_read), can_write: Boolean(p.can_write) })));
      setPermissions(updated);
      setRoleSuccess(true);
      const t = setTimeout(() => setRoleSuccess(false), 3000);
      return () => clearTimeout(t);
    } catch (e: any) {
      setPermError(e.message || 'Failed to save permissions');
    } finally {
      setSavingPerms(false);
    }
  };

  const saveRole = async () => {
    if (!editingRole) return;
    setSavingRole(true);
    try {
      const updated = await updateRole(editingRole.id, { name: roleForm.name, description: roleForm.description });
      setRoles((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setEditingRole(null);
      setRoleSuccess(true);
      const t = setTimeout(() => setRoleSuccess(false), 3000);
      return () => clearTimeout(t);
    } catch (e: any) {
      setRolesError(e.message || 'Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setShowDeleteSuccess(true);
      const t = setTimeout(() => setShowDeleteSuccess(false), 3000);
      return () => clearTimeout(t);
    } catch (e: any) {
      setError(e.message || 'Failed to delete user');
    }
  };

  useEffect(() => {
    if ((location.state as any)?.userUpdated) {
      setShowUpdateSuccess(true);
      navigate('.', { replace: true, state: {} });
      const t = setTimeout(() => setShowUpdateSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [location.state, navigate]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium mb-6">User and Role Management</h1>

      {/* Section 1: Create, Edit and Remove Users */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {showSuccess && (
          <div className="mb-4 rounded-md bg-green-50 p-4 border border-green-200">
            <div className="text-sm text-green-800">User created successfully.</div>
          </div>
        )}
        {showUpdateSuccess && (
          <div className="mb-4 rounded-md bg-green-50 p-4 border border-green-200">
            <div className="text-sm text-green-800">User updated successfully.</div>
          </div>
        )}
        {showDeleteSuccess && (
          <div className="mb-4 rounded-md bg-green-50 p-4 border border-green-200">
            <div className="text-sm text-green-800">User deleted successfully.</div>
          </div>
        )}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">User Management</h2>
          <button 
            onClick={() => navigate('/superadmin/users-roles/add-user')}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Add New User
          </button>
        </div>

        {/* User List Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="px-6 py-4" colSpan={5}>Loading...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td className="px-6 py-4 text-red-600" colSpan={5}>{error}</td>
                </tr>
              )}
              {!loading && !error && users.length === 0 && (
                <tr>
                  <td className="px-6 py-4" colSpan={5}>No users found.</td>
                </tr>
              )}
              {!loading && !error && users.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{u.firstName} {u.lastName}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{u.companyEmail}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{mapApiRoleToLabel(u.role)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button onClick={() => navigate(`/superadmin/users-roles/edit/${u.id}`)} className="text-blue-600 hover:text-blue-800 mr-3">Edit</button>
                    <button onClick={() => { setPasswordModalUser(u); setTempPassword(''); setPasswordError(''); }} className="text-amber-600 hover:text-amber-800 mr-3">Set Temp Password</button>
                    <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2: Access Control and Permissions */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-medium mb-6">Access Control and Permissions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Role List */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Roles</h3>
              <button 
                onClick={() => navigate('/superadmin/users-roles/add-role')}
                className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm"
              >
                Add New Role
              </button>
            </div>
            <div className="space-y-2">
              {rolesLoading && <div className="text-sm text-gray-500">Loading roles...</div>}
              {!rolesLoading && rolesError && <div className="text-sm text-red-600">{rolesError}</div>}
              {!rolesLoading && !rolesError && roles.map((role) => (
                <div key={role.id} className={`flex items-center justify-between p-2 rounded ${selectedRoleId === role.id ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                  <button onClick={() => selectRole(role)} className="text-left flex-1">{role.name}</button>
                  <button onClick={() => openEditRole(role)} className="text-gray-500 hover:text-gray-700 ml-2">Edit</button>
                </div>
              ))}
            </div>
            {roleSuccess && (
              <div className="mt-3 rounded bg-green-50 border border-green-200 p-2 text-sm text-green-800">Role updated successfully.</div>
            )}
          </div>

          {/* Permissions Grid */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Permissions</h3>
              <button 
                onClick={savePermissions}
                disabled={!selectedRoleId || savingPerms}
                className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm disabled:opacity-50"
              >
                {savingPerms ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            {!selectedRoleId && <div className="text-sm text-gray-500">Select a role to view permissions.</div>}
            {selectedRoleId && permLoading && <div className="text-sm text-gray-500">Loading permissions...</div>}
            {selectedRoleId && !permLoading && permError && <div className="text-sm text-red-600">{permError}</div>}
            {selectedRoleId && !permLoading && !permError && (
              <div className="space-y-3">
                {permissions.map((p) => (
                  <div key={p.module} className="flex items-center justify-between">
                    <span className="capitalize">{p.module.replace(/_/g, ' ')}</span>
                    <div className="space-x-2">
                      <input type="checkbox" checked={Boolean(p.can_read)} onChange={() => togglePerm(p.module, 'can_read')} className="rounded text-[#FF5722]" />
                      <label className="text-sm text-gray-600">Read</label>
                      <input type="checkbox" checked={Boolean(p.can_write)} onChange={() => togglePerm(p.module, 'can_write')} className="rounded text-[#FF5722]" />
                      <label className="text-sm text-gray-600">Write</label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {editingRole && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Edit Role</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditingRole(null)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={saveRole} disabled={savingRole} className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50">{savingRole ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {passwordModalUser && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Set Temporary Password</h3>
            {passwordError && <div className="mb-3 text-sm text-red-600">{passwordError}</div>}
            {passwordSuccess && <div className="mb-3 text-sm text-green-700">Temporary password set successfully.</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Temporary Password</label>
              <input
                type="password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter a new temporary password"
              />
              <p className="mt-1 text-xs text-gray-500">User will be asked to change it on first login.</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setPasswordModalUser(null); setTempPassword(''); setPasswordError(''); setPasswordSuccess(false); }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
              <button
                onClick={async () => {
                  setPasswordError('');
                  if (!tempPassword || tempPassword.length < 8) { setPasswordError('Password must be at least 8 characters.'); return; }
                  setSettingPassword(true);
                  try {
                    await setTemporaryPassword(passwordModalUser.id, tempPassword);
                    setPasswordSuccess(true);
                    const t = setTimeout(() => setPasswordSuccess(false), 2500);
                    // close after short delay
                    setTimeout(() => { setPasswordModalUser(null); setTempPassword(''); }, 800);
                    return () => clearTimeout(t);
                  } catch (e: any) {
                    setPasswordError(e.message || 'Failed to set temporary password');
                  } finally {
                    setSettingPassword(false);
                  }
                }}
                disabled={settingPassword}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
              >
                {settingPassword ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section 3: Monitor User Activity */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-medium mb-6">User Activity Monitor</h2>
        
        <div className="space-y-4">
          {/* Activity Filters */}
          <div className="flex flex-wrap gap-4 mb-4 items-center">
            <select
              value={loginFilters.userScope}
              onChange={(e) =>
                setLoginFilters((prev) => ({ ...prev, userScope: e.target.value as ActivityUserScope }))
              }
              className="border rounded-md px-3 py-1.5 text-sm"
            >
              <option value="all">All Users</option>
              <option value="active">Active Users</option>
              <option value="inactive">Inactive Users</option>
            </select>
            <select
              value={loginFilters.timeframe}
              onChange={(e) =>
                setLoginFilters((prev) => ({ ...prev, timeframe: e.target.value as ActivityTimeRange }))
              }
              className="border rounded-md px-3 py-1.5 text-sm"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <select
              value={loginFilters.module}
              onChange={(e) =>
                setLoginFilters((prev) => ({ ...prev, module: e.target.value as ActivityModuleFilter }))
              }
              className="border rounded-md px-3 py-1.5 text-sm"
            >
              <option value="all">All Activities</option>
              <option value="templates">Templates</option>
              <option value="campaigns">Campaigns</option>
              <option value="settings">Settings</option>
              <option value="groups">Groups</option>
            </select>
            <button
              onClick={() => setShowLoginResults(true)}
              className="px-4 py-2 bg-[#FF5722] text-white text-sm rounded-md hover:bg-[#FF5722]/90 transition-colors"
            >
              Filter Results
            </button>
          </div>

          {/* Login Activity */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-medium">Login Activity</h3>
              <div className="flex items-center gap-2">
                <button 
                  className="px-3 py-1.5 text-sm border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722]/5 inline-flex items-center transition-colors"
                >
                  Export as PDF
                </button>
                <button 
                  className="px-3 py-1.5 text-sm border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722]/5 inline-flex items-center transition-colors"
                >
                  Export as CSV
                </button>
              </div>
            </div>
            {!showLoginResults && (
              <div className="text-sm text-gray-500 py-6 border border-dashed rounded-lg text-center">
                Adjust filters and click "Filter Results" to preview login activity before exporting.
              </div>
            )}
            {showLoginResults && (
              <div className="space-y-3 mt-3">
                {loginActivityLoading ? (
                  <div className="text-sm text-gray-500">Loading login activity...</div>
                ) : loginActivityError ? (
                  <div className="text-sm text-red-600">{loginActivityError}</div>
                ) : loginActivities.length === 0 ? (
                  <div className="text-sm text-gray-500">No login activity found.</div>
                ) : (
                  loginActivities.map((activity) => (
                    <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{getActivityTitle(activity)}</p>
                        <p className="text-sm text-gray-600">{getActivityDescription(activity)}</p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatRelativeTime(activity.createdAt)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* System Changes Activity */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-medium">System Changes</h3>
              <div className="flex items-center gap-2">
                <button 
                  className="px-3 py-1.5 text-sm border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722]/5 inline-flex items-center transition-colors"
                >
                  Export as PDF
                </button>
                <button 
                  className="px-3 py-1.5 text-sm border border-[#FF5722] text-[#FF5722] rounded-md hover:bg-[#FF5722]/5 inline-flex items-center transition-colors"
                >
                  Export as CSV
                </button>
              </div>
            </div>

            {/* Added System Changes Filters */}
            <div className="flex flex-wrap gap-4 mb-4 items-center">
              <select
                value={systemFilters.userScope}
                onChange={(e) =>
                  setSystemFilters((prev) => ({ ...prev, userScope: e.target.value as ActivityUserScope }))
                }
                className="border rounded-md px-3 py-1.5 text-sm"
              >
                <option value="all">All Users</option>
                <option value="active">Active Users</option>
                <option value="inactive">Inactive Users</option>
              </select>
              <select
                value={systemFilters.timeframe}
                onChange={(e) =>
                  setSystemFilters((prev) => ({ ...prev, timeframe: e.target.value as ActivityTimeRange }))
                }
                className="border rounded-md px-3 py-1.5 text-sm"
              >
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
              <select
                value={systemFilters.module}
                onChange={(e) =>
                  setSystemFilters((prev) => ({ ...prev, module: e.target.value as ActivityModuleFilter }))
                }
                className="border rounded-md px-3 py-1.5 text-sm"
              >
                <option value="all">All Activities</option>
                <option value="templates">Templates</option>
                <option value="campaigns">Campaigns</option>
                <option value="settings">Settings</option>
                <option value="groups">Groups</option>
              </select>
              <button
                onClick={() => setShowSystemResults(true)}
                className="px-4 py-2 bg-[#FF5722] text-white text-sm rounded-md hover:bg-[#FF5722]/90 transition-colors"
              >
                Filter Results
              </button>
            </div>

            {!showSystemResults && (
              <div className="text-sm text-gray-500 py-6 border border-dashed rounded-lg text-center">
                Apply your filters and click "Filter Results" to reveal recent system activity before exporting.
              </div>
            )}
            {showSystemResults && (
              <div className="space-y-3 mt-3">
                {systemActivityLoading ? (
                  <div className="text-sm text-gray-500">Loading system changes...</div>
                ) : systemActivityError ? (
                  <div className="text-sm text-red-600">{systemActivityError}</div>
                ) : systemActivities.length === 0 ? (
                  <div className="text-sm text-gray-500">No recent system changes.</div>
                ) : (
                  systemActivities.map((activity) => (
                    <div key={activity.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{getActivityTitle(activity)}</p>
                          <p className="text-sm text-gray-600">{getActivityDescription(activity)}</p>
                          <div className="mt-1 text-xs text-gray-500">
                            <span className="inline-block px-2 py-1 bg-gray-100 rounded mr-2">
                              {getActivityBadge(activity)}
                            </span>
                            <span className="text-gray-400">{activity.userName}</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatRelativeTime(activity.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t">
            <div className="text-sm text-gray-500">
              Showing 1-8 of 24 activities
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1 border rounded-md text-sm text-gray-500 hover:bg-gray-50">
                Previous
              </button>
              <button className="px-3 py-1 border rounded-md text-sm bg-[#FF5722] text-white hover:bg-[#FF5722]/90">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRoleManagement; 